import {
  actionRequiresExistingCard,
  SUBSCRIPTION_ACTIONS,
} from "../hooks/checkoutFlow";

// SUB-001 / QA-017 fix: the subscription checkout Confirm button used to gate
// on `!defaultCardId` alone (no card on file), completely ignoring the actual
// price being charged. That blocked legitimate $0 signups (e.g. a validated
// 100%-off coupon) from ever completing, since a card was demanded regardless
// of the amount due.
//
// Revenue-blocker (T-FE-1): the same gate also blocked first-time SUBSCRIBE
// (Free → paid). That path uses Stripe Checkout and must NOT require a card on
// file before Confirm. Only UPGRADE/DOWNGRADE between paid tiers need an
// existing default card.

/**
 * Parses a price-like value (string or number) into a finite number, or
 * `null` if it can't be parsed. Deliberately does NOT coerce a missing value
 * to 0 -- callers need to tell "no price data" apart from "a genuine $0".
 */
export const parsePrice = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Returns the actual amount due for the currently selected plan/action,
 * accounting for any applied discount code. A validated 100%-off coupon
 * (`discountInfo.discountedPrice === 0`) means $0 is due even though the
 * plan's list price is nonzero.
 *
 * Returns `null` when the amount can't be determined (no plan selected and
 * no discount info) so callers can fail safe instead of assuming $0.
 */
export const getAmountDue = ({ selectedPlan, discountInfo }) => {
  if (discountInfo) {
    return parsePrice(discountInfo.discountedPrice);
  }
  if (!selectedPlan) return null;
  return parsePrice(selectedPlan.per_month_cost ?? selectedPlan.price?.price);
};

/**
 * Whether the checkout Confirm button should be disabled because a payment
 * method is required but none is on file yet.
 *
 * - A cancellation (downgrade to free) never requires a card.
 * - A $0 amount due (free tier, or a 100%-off coupon) never requires a card,
 *   regardless of whether one happens to be on file.
 * - Any nonzero amount due still requires a card, matching the pre-existing
 *   (pre-bug) behavior -- this is the "no regression" case.
 * - If the amount due can't be determined, fail safe and require a card
 *   rather than silently letting an unpriced purchase through.
 */
export const isCheckoutActionDisabled = ({
  selectedPlan,
  discountInfo,
  defaultCardId,
  hasDefaultPaymentMethod = false,
  isCardsLoading = false,
  isCancelAction = false,
}) => {
  if (!selectedPlan || isCancelAction) return false;

  const amountDue = getAmountDue({ selectedPlan, discountInfo });

  if (amountDue === 0) return false;

  const action = selectedPlan.action ?? SUBSCRIPTION_ACTIONS.SUBSCRIBE;

  // First paid signup: Stripe Checkout collects payment — never disable Confirm
  // for missing card on file.
  if (!actionRequiresExistingCard(action)) return false;

  if (isCardsLoading) return false;

  return !(defaultCardId || hasDefaultPaymentMethod);
};
